'use client';

import { queryClient } from '@lib/config';
import { AccountProvider } from '@lib/context/account-context';
import { CartDropdownProvider } from '@lib/context/cart-dropdown-context';
import { MobileMenuProvider } from '@lib/context/mobile-menu-context';
import { StoreProvider } from '@lib/context/store-context';
import { MedusaProvider, CartProvider } from 'medusa-react';
import { MaintenanceProvider } from '@lib/context/maintenance-context';
import { GiftCardsLimitProvider } from '@lib/context/gift-cards-limit-context';

import { ThemeProvider } from '@material-tailwind/react';
import { ConfigStoreClosedProvider } from '@lib/context/store-closed-context';
import { SearchServiceProvider } from '@lib/context/search-service-context';
import { KarunaProvider } from '@lib/context/kurana-context';
import { useConfig } from '@lib/context/config-context';

export default function Providers({ children }: { children: React.ReactNode }) {
  // Use the config from ConfigProvider context (set by layout.tsx)
  const config = useConfig();

  const customHeaders = {
    'x-api-key': config.CLIENT_API_KEY,
  };

  // Only log errors, not normal operation
  if (!config.MEDUSA_BACKEND_URL_CLIENT || !config.MEDUSA_BACKEND_URL_CLIENT.startsWith('http')) {
    console.error('[Providers] ❌ INVALID baseUrl for MedusaProvider:', config.MEDUSA_BACKEND_URL_CLIENT);
  }

  // Force remount of MedusaProvider if baseUrl changes to ensure new client instance
  return (
    <MedusaProvider
      key={config.MEDUSA_BACKEND_URL_CLIENT} // Force remount on URL change
      baseUrl={config.MEDUSA_BACKEND_URL_CLIENT}
      queryClientProviderProps={{
        client: queryClient,
      }}
      customHeaders={customHeaders}
      // Ensure credentials (cookies) are sent with all requests
      apiKey={config.MEDUSA_API_KEY}
    >
      <KarunaProvider>
        <CartDropdownProvider>
          <MobileMenuProvider>
            <SearchServiceProvider>
              <CartProvider>
                <AccountProvider>
                  <GiftCardsLimitProvider>
                    <StoreProvider>
                      <MaintenanceProvider>
                        <ConfigStoreClosedProvider>
                          <ThemeProvider>{children}</ThemeProvider>
                        </ConfigStoreClosedProvider>
                      </MaintenanceProvider>
                    </StoreProvider>
                  </GiftCardsLimitProvider>
                </AccountProvider>
              </CartProvider>
            </SearchServiceProvider>
          </MobileMenuProvider>
        </CartDropdownProvider>
      </KarunaProvider>
    </MedusaProvider>
  );
}
